import React, { useState, useMemo, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import qapi from "views_shared/app/javascript/api";
import { throwOnApiError } from "views_shared/app/javascript/api/api";
import {
  deserializeModel as deserializeExpert,
  modelFields as expertFields,
} from "../edit/experts_modal_body/serialization";
import { deserializeModel as deserializeEmployment } from "../edit/employment_modal_body/serialization";

const PrProfileContext = React.createContext(null);

// The provider owns both edit-collections (represented experts + employment) as the single source of
// truth — the show page renders them and the edit modals read/mutate them (no separate modal fetch).
// Both are stored DESERIALIZED (camelCase) through the edit-modal serializers, so the show grid + the
// modal share one shape. The experts field-list (`expertFields()`) includes `representatives`, which
// removeExpert needs; the employment fetch selects all view columns (logo/slug/featured included).

// Keep employment ordered featured/current-first (matches the server order in getAllByPrProfile), so an
// optimistic insert lands in the right place before silentReload reconciles. `from` is optional, so `id`
// is the TOTAL final key: without it, jobs with tied or missing start dates could swap places between
// reloads and drag every "first job" consumer (header company, Team Members roster) along with them.
function employmentsInvariant(rows) {
  return [...rows].sort((a, b) => {
    if (!!a.featured !== !!b.featured) return a.featured ? -1 : 1; // featured first
    const byStart = (b.from || "").localeCompare(a.from || ""); // then most-recent started_at first
    if (byStart !== 0) return byStart;
    return (a.id || 0) - (b.id || 0); // stable tiebreak, matching the server's id.asc final key
  });
}

// getResource RESOLVES with `{ error }` on a non-2xx — only a transport failure rejects. Reading
// `response.data` straight through would therefore turn a failed read into an empty collection and wipe
// the section (or blank the profile itself); throwing instead leaves state at its previous value on a
// nonfatal reload — which is what makes the action hooks' rollback survive a reconcile that also fails.
function readRows(response) {
  throwOnApiError(response);
  return Array.isArray(response.data) ? response.data : [];
}

// Collapse the representations view's per-job fan-out into one display row per promoted entity. Each
// kept row carries the entity's display fields plus every distinct representation id for it (normally
// one — a promoter represents a given entity once — but removal destroys all, matching removeExpert).
// companyName/companySlug/companyMigrateId (the view's promoted_company_name/_slug/_migrate_id) ride
// along additively for the Products modal's card subtitle — the Experts caller (which reads
// representedExpertS, not this function) never looks at them, and a company row simply carries them
// as undefined.
function dedupeRepresentations(rows) {
  const byEntity = new Map();
  rows.forEach((row) => {
    const entityId = row.promoted_entity_id;
    const existing = byEntity.get(entityId);
    if (existing) {
      if (!existing.representationIds.includes(row.id)) existing.representationIds.push(row.id);
      return;
    }
    byEntity.set(entityId, {
      id: entityId,
      name: row.promoted_entity_name,
      slug: row.promoted_entity_slug,
      migrateId: row.promoted_entity_migrate_id,
      profileImageUrl: row.promoted_entity_profile_image_url,
      companyName: row.promoted_company_name,
      companySlug: row.promoted_company_slug,
      companyMigrateId: row.promoted_company_migrate_id,
      representationIds: [row.id],
    });
  });
  return [...byEntity.values()];
}

// Shape the batched derived-tags read into `{ promoEntityId: [tag, ...] }` for the represents rows.
// The view fans one hashtag out per tagged object and per representing profile, so an entity's rows
// arrive duplicated by construction: dedupe each entity's by hashtag_id with a times_tagged count and
// rank most-tagged first, so a row that shows only its top few chips shows the top few rather than
// whichever ones PostgREST happened to return first. Reads the response directly rather than through
// readRows — chips are enrichment, and a failed read must leave the section alone.
function groupTagsByEntity(response) {
  const rows = Array.isArray(response.data) ? response.data : [];
  const byEntity = new Map();

  rows.forEach((row) => {
    const tags = byEntity.get(row.promo_entity_id) || [];
    const existing = tags.find((tag) => tag.hashtag_id === row.hashtag_id);
    if (existing) {
      existing.times_tagged += 1;
    } else {
      tags.push({ ...row, times_tagged: 1 });
    }
    byEntity.set(row.promo_entity_id, tags);
  });

  return Object.fromEntries(
    [...byEntity].map(([entityId, tags]) => [entityId, [...tags].sort((a, b) => b.times_tagged - a.times_tagged)]),
  );
}

// Merge a PR profile's derived (auto) hashtags with its explicitly-added (regular) ones into a
// single list, de-duplicated by hashtag_id with a times_tagged count, sorted most-tagged-first.
// Lifted verbatim from the retired usePrProfileState hook — this is the one piece of non-obvious
// data shaping on the page, so it stays identical.
function mergeHashtags(derivedTagsResponse, regularTagsResponse, prProfileId) {
  const allTags = [];

  if (Array.isArray(derivedTagsResponse.data) && derivedTagsResponse.data.length) {
    allTags.push(...derivedTagsResponse.data);
  }

  if (Array.isArray(regularTagsResponse.data) && regularTagsResponse.data.length) {
    const convertedTags = regularTagsResponse.data.map((tag) => ({
      hashtag_id: tag.hashtag_id,
      hashtag_name: tag.hashtag_name,
      hashtag_slug: tag.hashtag_slug,
      is_added: tag.is_added,
      profile_id: prProfileId,
      profile_type: "Id::Profile::Promoter",
      tagged_at: tag.tagged_at,
      tagged_id: tag.tagged_id,
      tagged_type: tag.tagged_type,
    }));
    allTags.push(...convertedTags);
  }

  if (allTags.length === 0) return [];

  return allTags
    .reduce((acc, current) => {
      const existing = acc.find((item) => item.hashtag_id === current.hashtag_id);
      if (existing) {
        existing.times_tagged += 1;
      } else {
        acc.push({ ...current, times_tagged: 1 });
      }
      return acc;
    }, [])
    .sort((a, b) => b.times_tagged - a.times_tagged);
}

// PR Profile's state provider — the Company provider/context pattern (the design we standardized
// on, replacing Ivan's per-component usePrProfileState hook). Holds the profile record, its
// represented experts + merged hashtags, the open-edit-modal key, and the viewerContext seed.
// Reuse note: the page's edit modals each instantiate use_pr_profile_update_api from this context's
// setters (mirroring Company's HeaderForm), so writes flow through one place.
function PrProfileProvider(props) {
  const { children } = props;

  // ViewerContext seed ({ editable, isAuthenticated, role }) computed server-side and threaded
  // ERB -> provider -> affordances, replacing the old bare `canEdit` boolean. Default {} so a
  // missing prop reads as non-editable.
  // eslint-disable-next-line react/destructuring-assignment
  const { viewerContext = {} } = props.value;

  // alphabetized
  const [activeTabRequest, setActiveTabRequest] = useState(null);
  const [asyncError, setAsyncError] = useState(null);
  const [employments, setEmploymentsRaw] = useState([]);
  const [generalError, setGeneralError] = useState(null);
  const [hashtags, setHashtags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(null);
  const [prProfile, setPrProfile] = useState(null);
  const [prProfileId, setPrProfileId] = useState(null);
  const [representedCompanies, setRepresentedCompanies] = useState([]);
  const [representedExpertTags, setRepresentedExpertTags] = useState({});
  const [representedExperts, setRepresentedExperts] = useState([]);
  const [representedProducts, setRepresentedProducts] = useState([]);

  // Invariant-preserving employment setter: the action hook mutates via this so optimistic inserts stay
  // ordered featured/current-first. Accepts a value or an updater (functional setState).
  const setEmployments = useCallback(
    (next) => setEmploymentsRaw((current) => employmentsInvariant(typeof next === "function" ? next(current) : next)),
    [],
  );

  // The full data load. `silent` skips the page-level loading flag so a save-triggered refetch
  // doesn't flash the skeleton (mirrors the old hook's silentReload vs userInitiatedReload split).
  // Callable (unlike Company's doTopLevelFetch flag) because the edit modal bodies `await
  // parentReload()` before closing.
  const loadProfile = useCallback(
    async ({ silent = false, nonfatal = false } = {}) => {
      if (!prProfileId) return;
      try {
        if (!silent) setIsLoading(true);

        const profilePromise = qapi.prProfiles.get(prProfileId).then((response) => {
          // Gate on the returned ROW, not the estimated count. getResource uses count=estimated, which
          // can misreport (it returned 1 for 75 rows once) — a bad estimate of 0 for a real profile would
          // otherwise set null and strand the page on "Loading…" forever.
          const rows = readRows(response);
          setPrProfile(rows.length ? rows[0] : null);
        });

        const expertsPromise = qapi.experts.getAllRepresentedByPr(prProfileId, expertFields()).then((response) => {
          const experts = readRows(response).map(deserializeExpert);
          setRepresentedExperts(experts);
          // The rows' chips are chained onto this read rather than fired from an effect on the
          // collection: the ids come from THIS response (state has not committed yet), and returning
          // the promise puts the chips inside the same barrier below, so a reload cannot resolve
          // before its own chips have landed. Enrichment, so it soft-fails to no chips at all rather
          // than rejecting the read the whole section depends on.
          return qapi.hashtags
            .getAllForPromoEntitiesForDisplay(
              experts.map((expert) => expert.id),
              "Id::PromoEntity::Expert",
            )
            .then((tagsResponse) => setRepresentedExpertTags(groupTagsByEntity(tagsResponse)))
            .catch(() => setRepresentedExpertTags({}));
        });

        const employmentsPromise = qapi.companyJobs.getAllByPrProfile(prProfileId).then((response) => {
          setEmployments(readRows(response).map(deserializeEmployment));
        });

        const productsPromise = qapi.representations.getProductsForPromoterProfileId(prProfileId).then((response) => {
          setRepresentedProducts(dedupeRepresentations(readRows(response)));
        });

        const companiesPromise = qapi.representations.getCompaniesForPromoterProfileId(prProfileId).then((response) => {
          setRepresentedCompanies(dedupeRepresentations(readRows(response)));
        });

        const tagsPromise = Promise.all([
          qapi.hashtags.getAllPrProfileDerivedTags(prProfileId),
          qapi.hashtags.getAllPrProfileTags(prProfileId),
        ])
          .then(([derived, regular]) => setHashtags(mergeHashtags(derived, regular, prProfileId)))
          .catch(() => setHashtags([]));

        if (nonfatal) {
          // Every started read must settle (and commit its state) before loadProfile resolves: callers
          // treat resolution as "all state writes are done". Promise.all rejects on the first rejection
          // while siblings are still in flight, so an early rejection here — with a superseded read still
          // pending — could let that late read commit stale state over newer state after a retry. Settle
          // all six, then rethrow the first rejection so the catch below keeps its exact behavior. (Five
          // of the six reads can reject; the tags read already self-absorbs its error via its own .catch.)
          const results = await Promise.allSettled([
            profilePromise,
            expertsPromise,
            employmentsPromise,
            productsPromise,
            companiesPromise,
            tagsPromise,
          ]);
          const rejected = results.find((r) => r.status === "rejected");
          if (rejected) throw rejected.reason;
        } else {
          // Fatal loads (initial page load, post-save refresh) must surface the error boundary
          // immediately rather than idle on the skeleton behind a stalled sibling read (the fetch layer
          // has no timeout). Fail fast on the first rejection. The stale-late-write race doesn't apply
          // here because a fatal reload failure tears the page down — there is no retry surface to race.
          await Promise.all([
            profilePromise,
            expertsPromise,
            employmentsPromise,
            productsPromise,
            companiesPromise,
            tagsPromise,
          ]);
        }
      } catch (error) {
        // A nonfatal reload is used when reconciling after a failed save — a failed refresh there must
        // not escalate a recoverable modal error into the page error boundary (asyncError is re-thrown).
        if (!nonfatal) setAsyncError(error);
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [prProfileId, setEmployments],
  );

  // Initial load (and reload whenever the id changes).
  useEffect(() => {
    if (prProfileId) loadProfile();
  }, [prProfileId, loadProfile]);

  // Silent refetch the edit modals call after a successful save. Forwards options so a caller can mark
  // the reload nonfatal (e.g. reconciling after a failed save); default call sites are unaffected.
  const silentReload = useCallback((opts = {}) => loadProfile({ silent: true, ...opts }), [loadProfile]);

  const providerValue = useMemo(
    () => ({
      // alphabetized
      activeTabRequest,
      asyncError,
      employments,
      generalError,
      hashtags,
      isLoading,
      isUpdating,
      openEditModal,
      prProfile,
      prProfileId,
      representedCompanies,
      representedExpertTags,
      representedExperts,
      representedProducts,
      setActiveTabRequest,
      setAsyncError,
      setEmployments,
      setGeneralError,
      setIsUpdating,
      setOpenEditModal,
      setPrProfileId,
      setRepresentedCompanies,
      setRepresentedExperts,
      setRepresentedProducts,
      silentReload,
      viewerContext,
    }),
    [
      activeTabRequest,
      asyncError,
      employments,
      generalError,
      hashtags,
      isLoading,
      isUpdating,
      openEditModal,
      prProfile,
      prProfileId,
      representedCompanies,
      representedExpertTags,
      representedExperts,
      representedProducts,
      setEmployments,
      silentReload,
      viewerContext,
    ],
  );

  return <PrProfileContext.Provider value={providerValue}>{children}</PrProfileContext.Provider>;
}

export { PrProfileContext, PrProfileProvider };

PrProfileProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};
